// Timeline types - Life events and phases for the visual timeline

// ============================================================================
// LIFE EVENT TYPES
// ============================================================================

export type LifeEventCategory = 
  | 'career'
  | 'family'
  | 'housing'
  | 'education'
  | 'retirement'
  | 'financial'
  | 'health'
  | 'custom'

export type LifeEventType = 
  // Career events
  | 'career_start'
  | 'promotion'
  | 'job_change'
  | 'salary_increase'
  | 'sabbatical'
  | 'career_end'
  // Family events
  | 'marriage'
  | 'divorce'
  | 'child_born'
  | 'child_college_start'
  | 'child_college_end'
  | 'child_independent'
  // Housing events
  | 'home_purchase'
  | 'home_sale'
  | 'renovation'
  | 'relocation'
  // Education events
  | 'education_start'
  | 'education_end'
  // Retirement events
  | 'retirement_start'
  | 'social_security_start'
  | 'pension_start'
  | 'rmd_start'              // Required Minimum Distributions
  // Financial events
  | 'inheritance'
  | 'windfall'
  | 'major_purchase'
  | 'debt_payoff'
  | 'goal_achieved'
  // Health events
  | 'disability'
  | 'long_term_care'
  // Custom
  | 'custom'

// ============================================================================
// FINANCIAL IMPACT
// ============================================================================

export interface FinancialImpact {
  // One-time impacts
  oneTimeIncome?: number         // Positive: inheritance, bonus, sale
  oneTimeExpense?: number        // Positive value for expenses
  
  // Recurring changes (annual amounts)
  incomeChange?: number          // Delta to annual income (can be negative)
  expenseChange?: number         // Delta to annual expenses (can be negative)
  savingsChange?: number         // Delta to annual savings
  
  // Asset/debt changes
  assetChange?: number           // Net change to assets
  debtChange?: number            // Net change to debt (positive = more debt)
}

// ============================================================================
// LIFE EVENT
// ============================================================================

export interface LifeEvent {
  id: string
  type: LifeEventType
  category: LifeEventCategory
  name: string
  description?: string
  
  // Timing
  date: string                   // ISO date
  endDate?: string               // For events with duration (sabbatical, college)
  isRecurring: boolean
  recurringFrequency?: 'monthly' | 'quarterly' | 'annually'
  
  // Financial impact
  financialImpact: FinancialImpact
  
  // Visual
  color?: string
  icon?: string                  // Lucide icon name
  
  // Links
  linkedGoalId?: string
  linkedChildId?: string
  linkedAssetId?: string
  linkedDebtId?: string
  
  // Metadata
  isAutoGenerated: boolean       // System-generated vs user-created
  isPast: boolean
  confidence: 'certain' | 'likely' | 'possible'
  notes?: string
}

// ============================================================================
// LIFE PHASE (Budget Periods)
// ============================================================================

export type LifePhaseType = 
  | 'student'
  | 'early_career'
  | 'career_growth'
  | 'peak_earning'
  | 'pre_retirement'
  | 'early_retirement'
  | 'full_retirement'
  | 'custom'

export interface PhaseBudget {
  // Income
  expectedIncome: number         // Annual
  incomeGrowthRate: number       // % per year
  
  // Fixed expenses
  housing: number
  utilities: number
  insurance: number
  transportation: number
  
  // Variable expenses
  food: number
  healthcare: number
  childcare: number
  education: number
  entertainment: number
  travel: number
  clothing: number
  personalCare: number
  
  // Savings
  retirement401k: number
  retirementIRA: number
  taxableSavings: number
  emergencyFund: number
  
  // Other
  debtPayments: number
  charitable: number
  other: number
}

export interface LifePhase {
  id: string
  type: LifePhaseType
  name: string
  description?: string
  
  // Timing
  startDate: string
  endDate: string
  
  // Budget for this phase
  budget: PhaseBudget
  
  // Visual
  color: string
  
  // Metadata
  isActive: boolean              // Is current phase
  isAutoGenerated: boolean
}

// ============================================================================
// TIMELINE STATE
// ============================================================================

export interface TimelineState {
  events: LifeEvent[]
  phases: LifePhase[]
  
  // View settings
  viewStartYear: number
  viewEndYear: number
  selectedEventId: string | null
  selectedPhaseId: string | null
  zoomLevel: 'decade' | 'five_year' | 'year' | 'month'
  
  // Filters
  showPastEvents: boolean
  visibleCategories: LifeEventCategory[]
}

// ============================================================================
// HELPERS
// ============================================================================

export const EVENT_CATEGORY_MAP: Record<LifeEventType, LifeEventCategory> = {
  career_start: 'career',
  promotion: 'career',
  job_change: 'career',
  salary_increase: 'career',
  sabbatical: 'career',
  career_end: 'career',
  marriage: 'family',
  divorce: 'family',
  child_born: 'family',
  child_college_start: 'family',
  child_college_end: 'family',
  child_independent: 'family',
  home_purchase: 'housing',
  home_sale: 'housing',
  renovation: 'housing',
  relocation: 'housing',
  education_start: 'education',
  education_end: 'education',
  retirement_start: 'retirement',
  social_security_start: 'retirement',
  pension_start: 'retirement',
  rmd_start: 'retirement',
  inheritance: 'financial',
  windfall: 'financial',
  major_purchase: 'financial',
  debt_payoff: 'financial',
  goal_achieved: 'financial',
  disability: 'health',
  long_term_care: 'health',
  custom: 'custom',
}

export const EVENT_ICONS: Record<LifeEventType, string> = {
  career_start: 'Briefcase',
  promotion: 'TrendingUp',
  job_change: 'ArrowRightLeft',
  salary_increase: 'DollarSign',
  sabbatical: 'Palmtree',
  career_end: 'LogOut',
  marriage: 'Heart',
  divorce: 'HeartCrack',
  child_born: 'Baby',
  child_college_start: 'GraduationCap',
  child_college_end: 'Award',
  child_independent: 'UserCheck',
  home_purchase: 'Home',
  home_sale: 'Building',
  renovation: 'Hammer',
  relocation: 'Truck',
  education_start: 'BookOpen',
  education_end: 'GraduationCap',
  retirement_start: 'Sunset',
  social_security_start: 'Landmark',
  pension_start: 'PiggyBank',
  rmd_start: 'ArrowDownToLine',
  inheritance: 'Gift',
  windfall: 'Sparkles',
  major_purchase: 'ShoppingCart',
  debt_payoff: 'CheckCircle',
  goal_achieved: 'Trophy',
  disability: 'Accessibility',
  long_term_care: 'HeartPulse',
  custom: 'Star',
}

export const PHASE_COLORS: Record<LifePhaseType, string> = {
  student: '#8b5cf6',           // violet
  early_career: '#3b82f6',      // blue
  career_growth: '#10b981',     // emerald
  peak_earning: '#f59e0b',      // amber
  pre_retirement: '#f97316',    // orange
  early_retirement: '#ef4444',  // red
  full_retirement: '#6366f1',   // indigo
  custom: '#64748b',            // slate
}

export const CATEGORY_COLORS: Record<LifeEventCategory, string> = {
  career: '#3b82f6',            // blue
  family: '#ec4899',            // pink
  housing: '#10b981',           // emerald
  education: '#8b5cf6',         // violet
  retirement: '#f59e0b',        // amber
  financial: '#22c55e',         // green
  health: '#ef4444',            // red
  custom: '#64748b',            // slate
}

// ============================================================================
// INITIAL STATE FACTORY
// ============================================================================

export const createInitialTimelineState = (): TimelineState => ({
  events: [],
  phases: [],
  viewStartYear: new Date().getFullYear(),
  viewEndYear: new Date().getFullYear() + 50,
  selectedEventId: null,
  selectedPhaseId: null,
  zoomLevel: 'decade',
  showPastEvents: true,
  visibleCategories: ['career', 'family', 'housing', 'education', 'retirement', 'financial', 'health', 'custom'],
})

// ============================================================================
// WEALTH PROJECTION POINT
// ============================================================================

export interface WealthProjectionPoint {
  year: number
  date: string
  age: number
  
  // Totals
  netWorth: number               // In real dollars
  nominalNetWorth: number        // In nominal dollars
  
  // Breakdown
  totalAssets: number
  totalDebts: number
  liquidAssets: number
  retirementAssets: number
  realEstateEquity: number
  
  // Cash flow
  annualIncome: number
  annualExpenses: number
  annualSavings: number
  
  // Events at this point
  eventsThisYear: string[]       // Event IDs
  phaseId: string                // Current phase
  
  // Confidence bands
  optimistic: number             // 90th percentile
  pessimistic: number            // 10th percentile
}

export interface WealthProjection {
  points: WealthProjectionPoint[]
  startYear: number
  endYear: number
  assumptions: {
    inflationRate: number
    stockReturn: number
    bondReturn: number
    realEstateAppreciation: number
  }
}
